import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import { useToast } from "../hooks/useToast";

import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  CircularProgress,
  Chip,
  Collapse,
  IconButton,
  TextField,
  InputAdornment,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import BusinessIcon from "@mui/icons-material/Business";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NavigationIcon from "@mui/icons-material/Navigation";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { MapContainer, TileLayer, Marker, LayersControl, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export const CentrosEmpleado = () => {
  const toast = useToast();

  const [clientes, setClientes] = useState([]);
  const [centros, setCentros] = useState([]);
  const [clienteAbierto, setClienteAbierto] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalMapa, setModalMapa] = useState(false);
  const [centroMapa, setCentroMapa] = useState(null);

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarDatos = async () => {
    setLoading(true);

    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

      const [resClientes, resCentros] = await Promise.all([
        fetch(`${Global.url}clientes`, { headers }),
        fetch(`${Global.url}centros`, { headers }),
      ]);

      const clientesData = resClientes.ok ? await resClientes.json().catch(() => []) : [];
      const centrosData = resCentros.ok ? await resCentros.json().catch(() => []) : [];

      if (!resClientes.ok) toast.error("No se pudieron cargar los clientes.");
      if (!resCentros.ok) toast.error("No se pudieron cargar los centros.");

      setClientes(Array.isArray(clientesData) ? clientesData : []);
      setCentros(Array.isArray(centrosData) ? centrosData : []);
    } catch (error) {
      console.error("Error al obtener clientes/centros:", error);
      toast.error("Error de conexión al cargar los centros.");
      setClientes([]);
      setCentros([]);
    } finally {
      setLoading(false);
    }
  };

  const tieneCoordenadas = (centro) => {
    const lat = Number(centro?.latitud);
    const lng = Number(centro?.longitud);
    return Number.isFinite(lat) && Number.isFinite(lng);
  };

  const abrirEnGPS = (lat, lng) => {
    const nLat = Number(lat);
    const nLng = Number(lng);

    if (!Number.isFinite(nLat) || !Number.isFinite(nLng)) {
      toast.warning("Este centro no tiene coordenadas asignadas.");
      return;
    }

    window.open(
      `https://www.google.com/maps/search/?api=1&query=${nLat},${nLng}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const abrirMapaInterno = (centro) => {
    if (!tieneCoordenadas(centro)) {
      toast.warning("Este centro no tiene coordenadas asignadas.");
      return;
    }

    setCentroMapa(centro);
    setModalMapa(true);
  };

  const cerrarMapaInterno = () => {
    setModalMapa(false);
    setCentroMapa(null);
  };

  const centrosPorCliente = useMemo(() => {
    const map = new Map();

    for (const centro of centros || []) {
      const key = String(centro.cliente_id);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(centro);
    }

    for (const [key, lista] of map.entries()) {
      map.set(
        key,
        lista.slice().sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
      );
    }

    return map;
  }, [centros]);

  const clientesFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();

    return (clientes || [])
      .filter((cliente) => {
        if (!q) return true;

        const nombreCliente = (cliente.nombre || "").toLowerCase();
        const listaCentros = centrosPorCliente.get(String(cliente.id)) || [];

        return (
          nombreCliente.includes(q) ||
          listaCentros.some((centro) => (centro.nombre || "").toLowerCase().includes(q))
        );
      })
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [clientes, centrosPorCliente, busqueda]);

  const mapCenter =
    centroMapa && tieneCoordenadas(centroMapa)
      ? [Number(centroMapa.latitud), Number(centroMapa.longitud)]
      : [40.4168, -3.7038];

  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Centros de Trabajo
        </Typography>

        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Buscar cliente o centro"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Escribe para filtrar…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

          </Stack>
        </Paper>

        {loading ? (
          <Paper sx={{ p: 4 }}>
            <Stack spacing={2} alignItems="center">
              <CircularProgress size={28} />
              <Typography color="text.secondary">Cargando centros…</Typography>
            </Stack>
          </Paper>
        ) : clientesFiltrados.length === 0 ? (
          <Paper sx={{ p: 2 }}>
            <Typography color="text.secondary">
              No hay clientes o centros que coincidan con la búsqueda.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {clientesFiltrados.map((cliente) => {
              const listaCentros = centrosPorCliente.get(String(cliente.id)) || [];
              const abierto = clienteAbierto === cliente.id;

              return (
                <Paper key={cliente.id}>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => setClienteAbierto((prev) => (prev === cliente.id ? null : cliente.id))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setClienteAbierto((prev) => (prev === cliente.id ? null : cliente.id));
                      }
                    }}
                    sx={{
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      cursor: "pointer",
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <BusinessIcon color="primary" />
                      <Box>
                        <Typography sx={{ fontWeight: 700 }}>{cliente.nombre}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {listaCentros.length} centro{listaCentros.length !== 1 ? "s" : ""}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label={abierto ? "Ocultar" : "Mostrar"} variant="outlined" />
                      <IconButton size="small">
                        {abierto ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Stack>
                  </Box>

                  <Collapse in={abierto} timeout="auto" unmountOnExit>
                    <Divider />

                    {listaCentros.length === 0 ? (
                      <Box sx={{ p: 2 }}>
                        <Typography color="text.secondary">
                          Este cliente no tiene centros disponibles.
                        </Typography>
                      </Box>
                    ) : (
                      <Stack spacing={1} sx={{ p: 2 }}>
                        {listaCentros.map((centro) => {
                          const coordsOk = tieneCoordenadas(centro);

                          return (
                            <Paper key={centro.id} variant="outlined" sx={{ p: 2 }}>
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                sx={{
                                  alignItems: { sm: "center" },
                                  justifyContent: "space-between",
                                }}
                              >
                                <Box>
                                  <Typography sx={{ fontWeight: 700 }}>
                                    {centro.nombre}
                                  </Typography>

                                  {coordsOk ? (
                                    <Typography variant="body2" color="text.secondary">
                                      {Number(centro.latitud)}, {Number(centro.longitud)}
                                    </Typography>
                                  ) : (
                                    <Typography variant="body2" color="error">
                                      Sin coordenadas asignadas
                                    </Typography>
                                  )}
                                </Box>

                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                  <Button
                                    variant="outlined"
                                    startIcon={<LocationOnIcon />}
                                    disabled={!coordsOk}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      abrirMapaInterno(centro);
                                    }}
                                  >
                                    Ver mapa
                                  </Button>

                                  <Button
                                    startIcon={<NavigationIcon />}
                                    disabled={!coordsOk}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      abrirEnGPS(centro.latitud, centro.longitud);
                                    }}
                                  >
                                    Navegar
                                  </Button>
                                </Stack>
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    )}
                  </Collapse>
                </Paper>
              );
            })}
          </Stack>
        )}

        <Dialog open={modalMapa} onClose={cerrarMapaInterno} fullWidth maxWidth="md">
          <DialogTitle>{centroMapa?.nombre || "Ubicación del centro"}</DialogTitle>
          <Divider />

          <DialogContent sx={{ p: 2 }}>
            {centroMapa && tieneCoordenadas(centroMapa) ? (
              <Box sx={{ width: "100%", height: 460 }}>
                <MapContainer center={mapCenter} zoom={16} style={{ height: "100%", width: "100%" }}>
                  <LayersControl position="topright">
                    <LayersControl.BaseLayer checked name="Satélite + Callejero">
                      <TileLayer
                        url={`https://api.maptiler.com/maps/hybrid/256/{z}/{x}/{y}.jpg?key=${process.env.REACT_APP_MAPTILER_KEY}&language=es`}
                        attribution='&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a>'
                      />
                    </LayersControl.BaseLayer>

                    <LayersControl.BaseLayer name="Callejero">
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution="&copy; OpenStreetMap contributors"
                      />
                    </LayersControl.BaseLayer>
                  </LayersControl>

                  <Marker position={mapCenter}>
                    <Tooltip direction="top" offset={[0, -10]}>
                      🏢 {centroMapa.nombre}
                    </Tooltip>
                  </Marker>
                </MapContainer>
              </Box>
            ) : (
              <Typography color="text.secondary">
                No hay coordenadas disponibles para este centro.
              </Typography>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={cerrarMapaInterno}>
              Cerrar
            </Button>

            {centroMapa && tieneCoordenadas(centroMapa) && (
              <Button
                startIcon={<NavigationIcon />}
                onClick={() => abrirEnGPS(centroMapa.latitud, centroMapa.longitud)}
              >
                Navegar
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};

export default CentrosEmpleado;